import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { CareComponentFilterSetting } from "../Models/CareComponentFilterSetting";
import { CareElementFrequencyType } from "../Models/CareElementFrequencyType";
import { VitalSignCareElement } from "../Models/CareElements/VitalSignCareElement";
import { VitalSignExam } from "../Models/CareElements/VitalSignExam";
import { ResultsDurationType } from "../Models/ResultsDurationType";
import { CareElementGridHelper } from "./CareElementGridHelper";
import { VitalSignCareElementGridHelper } from "./VitalSignCareElementGridHelper";

interface FlattenedVitalSignExam {
    exam: VitalSignExam;
    parentName: string;
    frequency: CareElementFrequencyType;
    isOutsideTarget: boolean;
}

export class FlowsheetVitalSignFilterHelper {
    public static filterCareElements(
        careElements: VitalSignCareElement[],
        careComponentFilterSettings: CareComponentFilterSetting,
        encounterDate: Date
    ): VitalSignCareElement[] {
        const enabledElements: VitalSignCareElement[] = careElements.filter(el =>
            careComponentFilterSettings.CareElements.some(x => x.Key === el.Name && x.IsEnabled)
        );

        if (enabledElements.every(element => element.Exams.length === 0))
            return enabledElements;

        const flattened: FlattenedVitalSignExam[] = enabledElements.flatMap(el => (el.Exams ?? []).map(exam => {
            const isOutsideTarget = careComponentFilterSettings.OutsideTreatmentTarget
                ? FlowsheetVitalSignFilterHelper.isOutsideTarget(el, exam)
                : false;

            return {
                exam,
                parentName: el.Name,
                frequency: el.Frequency,
                isOutsideTarget
            };
        }));

        const filteredByDate = this.filterByDateRange(flattened, careComponentFilterSettings);
        const filteredByTarget = this.filterByOutsideTreatmentTarget(filteredByDate, careComponentFilterSettings);
        const filteredByFrequency = this.filterByOutsideTreatmentFrequency(filteredByTarget, careComponentFilterSettings, encounterDate);

        const finalExams = this.filterByDurationType(filteredByFrequency, careComponentFilterSettings);

        const grouped: Map<string, VitalSignExam[]> = new Map();
        for (const entry of finalExams) {
            if (!grouped.has(entry.parentName)) {
                grouped.set(entry.parentName, []);
            }
            grouped.get(entry.parentName)!.push(entry.exam);
        }

        return enabledElements
            .map(el => ({
                ...el,
                Exams: grouped.get(el.Name) ?? []
            }));
    }

    private static filterByDateRange(
        exams: FlattenedVitalSignExam[],
        careComponentFilterSettings: CareComponentFilterSetting
    ): FlattenedVitalSignExam[] {
        const start = careComponentFilterSettings.StartDate
            ? new DateTimeWithPrecision(careComponentFilterSettings.StartDate, "", LifeStageDatePrecisionEnum.Day).value
            : undefined;

        const end = careComponentFilterSettings.EndDate
            ? new DateTimeWithPrecision(careComponentFilterSettings.EndDate, "", LifeStageDatePrecisionEnum.Day).value
            : undefined;

        return exams.filter(({ exam }) => {
            const examDate = this.toStartOfDay(new Date(exam.Date));
            return (!start || examDate >= this.toStartOfDay(start)) && (!end || examDate <= this.toStartOfDay(end));
        });
    }

    private static toStartOfDay(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    private static filterByDurationType(
        exams: FlattenedVitalSignExam[],
        careComponentFilterSettings: CareComponentFilterSetting
    ): FlattenedVitalSignExam[] {
        const groupedByDate: Map<string, FlattenedVitalSignExam[]> = new Map();

        for (const exam of exams) {
            const dateKey = new Date(exam.exam.Date).toISOString();
            if (!groupedByDate.has(dateKey)) {
                groupedByDate.set(dateKey, []);
            }
            groupedByDate.get(dateKey)!.push(exam);
        }

        const sortedDates = Array.from(groupedByDate.keys()).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
        const sliceCount = FlowsheetVitalSignFilterHelper.getSliceCount(careComponentFilterSettings.ResultsDurationType);
        const selectedDates = sliceCount ? sortedDates.slice(0, sliceCount) : sortedDates;

        return selectedDates.flatMap(date => groupedByDate.get(date)!);
    }

    private static getSliceCount(duration: ResultsDurationType): number | null {
        switch (duration) {
            case ResultsDurationType.Latest:
                return 1;
            case ResultsDurationType.Last2:
                return 2;
            case ResultsDurationType.Last3:
                return 3;
            default:
                return null;
        }
    }

    private static isOutsideTarget(
        careElement: VitalSignCareElement,
        exam: VitalSignExam
    ): boolean {
        switch (careElement.Name) {
            case "BloodPressure":
                return VitalSignCareElementGridHelper.isBloodPressureOutOfRange(
                    exam.Value,
                    exam.Value2,
                    careElement.TargetRange.LowerBound,
                    careElement.TargetRange.UpperBound,
                    careElement.Operator
                );
            default:
                return CareElementGridHelper.isValueOutOfRange(exam.Value, careElement);
        }
    }

    private static filterByOutsideTreatmentTarget(
        exams: FlattenedVitalSignExam[],
        careComponentFilterSettings: CareComponentFilterSetting
    ): FlattenedVitalSignExam[] {
        if (!careComponentFilterSettings.OutsideTreatmentTarget)
            return exams;

        return exams.filter(entry => entry.isOutsideTarget);
    }

    private static filterByOutsideTreatmentFrequency(
        exams: FlattenedVitalSignExam[],
        careComponentFilterSettings: CareComponentFilterSetting,
        encounterDate: Date
    ): FlattenedVitalSignExam[] {
        if (!careComponentFilterSettings.OutsideTreatmentFrequency)
            return exams;

        const grouped = exams.reduce((acc, curr) => {
            if (!acc.has(curr.parentName)) acc.set(curr.parentName, []);
            acc.get(curr.parentName)!.push(curr.exam);
            return acc;
        }, new Map<string, VitalSignExam[]>());

        const allowedParentNames = Array.from(grouped.entries())
            .filter(([parentName, values]) => {
                const frequency = exams.find(e => e.parentName === parentName)?.frequency ?? CareElementFrequencyType.None;
                return VitalSignCareElementGridHelper.shouldShowFrequencyWarningBadge(frequency, encounterDate, values);
            })
            .map(([parentName]) => parentName);

        const filteredByFrequency = exams.filter(e => allowedParentNames.includes(e.parentName));

        return filteredByFrequency.filter(e => e.exam.Value !== null);
    }
}
